import { useEffect, useState } from 'react'
import { fetchVariants, type VariantsResponse, type VariantStats } from '../../lib/api'
import type { Audience, Variant } from '../../types'

interface Props { range: { from: number; to: number } }

const AUDIENCE_LABEL: Record<Audience, string> = {
  unge: 'Unge',
  aeldre: 'Ældre',
}

const fmt = (n: number) => n.toLocaleString('da-DK')
const fmtPct = (p: number) => `${(p * 100).toFixed(1)}%`

interface AudienceBucket {
  audience: Audience
  variants: VariantStats[]
  pValue: number | null
  significant: boolean
  sampleSizeNeeded: number | null
  topDropoff: Record<Variant, { step: string; rate: number } | null>
}

interface VariantsResponseV2 extends VariantsResponse {
  audiences?: AudienceBucket[]
}

interface AudienceTotals {
  audience: Audience
  sessions: number
  leads: number
  rate: number
}

function totalsFromBucket(b: AudienceBucket): AudienceTotals {
  const sessions = b.variants.reduce((acc, v) => acc + v.sessions, 0)
  const leads = b.variants.reduce((acc, v) => acc + v.completions, 0)
  return {
    audience: b.audience,
    sessions,
    leads,
    rate: sessions > 0 ? leads / sessions : 0,
  }
}

export function AudienceComparison({ range }: Props) {
  const [data, setData] = useState<VariantsResponseV2 | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    // Always fetch unfiltered — this widget compares across audiences.
    fetchVariants(range)
      .then((d) => { if (!cancelled) setData(d as VariantsResponseV2) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [range.from, range.to])

  if (error) {
    return (
      <div className="card card-empty">
        Kunne ikke hente målgruppe-data
      </div>
    )
  }
  if (!data) {
    return <div className="card card-skeleton" aria-busy />
  }

  const buckets: AudienceBucket[] = data.audiences ?? []
  if (buckets.length === 0) {
    return (
      <div className="card card-empty">
        Ingen målgruppe-data tilgængelig endnu
      </div>
    )
  }

  const totals = buckets.map(totalsFromBucket)
  const totalSessions = totals.reduce((acc, t) => acc + t.sessions, 0)
  const totalLeads = totals.reduce((acc, t) => acc + t.leads, 0)
  const overallRate = totalSessions > 0 ? totalLeads / totalSessions : 0

  // Find best CR for visual bar scaling
  const maxRate = Math.max(0.01, ...totals.map((t) => t.rate))

  // Stable sort: unge first, then aeldre (matches reading order in NorthStar)
  const order: Audience[] = ['unge', 'aeldre']
  const sorted = [...totals].sort((a, b) => order.indexOf(a.audience) - order.indexOf(b.audience))

  return (
    <div className="card audience-comparison">
      <div className="card-header">
        <div className="card-title">Målgrupper</div>
        <div className="card-subtitle">
          Sammenligning af unge vs ældre · samlet {fmt(totalSessions)} sessioner og {fmt(totalLeads)} leads ({fmtPct(overallRate)})
        </div>
      </div>
      <div className="audience-grid">
        {sorted.map((t) => (
          <div key={t.audience} className={`audience-card audience-${t.audience}`}>
            <div className="audience-card-head">
              <span className={`pill audience-${t.audience}`}>{AUDIENCE_LABEL[t.audience]}</span>
              <span className="muted small">
                {totalSessions > 0
                  ? `${fmtPct(t.sessions / totalSessions)} af trafik`
                  : '—'}
              </span>
            </div>
            <div className="audience-rate">
              <div className="audience-rate-num">{fmtPct(t.rate)}</div>
              <div className="muted small">conversion rate</div>
            </div>
            <div className="audience-bar-track">
              <div
                className={`audience-bar-fill audience-bar-${t.audience}`}
                style={{ width: `${(t.rate / maxRate) * 100}%` }}
              />
            </div>
            <dl className="audience-stats">
              <div><dt>Sessioner</dt><dd>{fmt(t.sessions)}</dd></div>
              <div><dt>Leads</dt><dd>{fmt(t.leads)}</dd></div>
            </dl>
          </div>
        ))}
      </div>
    </div>
  )
}
